import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { Table2, MousePointerClick } from 'lucide-react';
import { getDecisionTableColumns, getRuleInputValue } from '@bliks-io/ui';
import type { StepDecisionRuleInput } from '@bliks-io/contracts';

/** Minimal rule shape the canvas decision-table reads — the recording's `rules`
 *  (column names come from each rule's `inputs`). */
type PreviewRule = {
  _id?: string;
  inputs?: StepDecisionRuleInput[];
  output?: string;
};

interface DecisionTablePreviewProps {
  rules?: PreviewRule[] | null;
  /** Clicking the preview opens the full decision table dialog. */
  onOpenDecisionTable: () => void;
}

/**
 * Decision table preview rendered on the recording canvas: clicking it (or the
 * empty-state call to action when there are no rules yet) opens the full
 * decision table dialog.
 */
export function DecisionTablePreview({ rules, onOpenDecisionTable }: DecisionTablePreviewProps) {
  const { t } = useTranslation();

  // Extract all unique column names from all rules' inputs
  const columnNames = useMemo(() => {
    if (!rules || rules.length === 0) return [];
    return getDecisionTableColumns(rules);
  }, [rules]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenDecisionTable();
    }
  };

  // No rules yet → a click-to-configure call to action.
  if (!rules || rules.length === 0) {
    return (
      <div
        className="group p-6 border-2 border-dashed border-line-strong rounded-lg bg-surface-subtle/50 cursor-pointer hover:bg-info-surface/50 hover:border-info-solid transition-all duration-200"
        onClick={onOpenDecisionTable}
        role="button"
        tabIndex={0}
        onKeyDown={onKeyDown}
      >
        <div className="flex flex-col items-center justify-center gap-3 text-center">
          <div className="relative">
            <Table2 className="h-8 w-8 text-fg-subtle group-hover:text-info transition-colors" />
            <MousePointerClick className="h-4 w-4 text-fg-subtle group-hover:text-info absolute -bottom-1 -right-1 transition-colors" />
          </div>
          <div>
            <p className="text-sm font-medium text-fg-secondary group-hover:text-info transition-colors">
              {t('viralRecording.modals.decisionTable.configureRulesDescription')}
            </p>
            <p className="text-xs text-muted-foreground mt-1 group-hover:text-info transition-colors">
              {t('viralRecording.labels.clickToOpen') || 'Click to open decision table'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="group p-4 border border-line rounded-lg bg-surface-subtle cursor-pointer hover:bg-info-surface hover:border-info-solid transition-all duration-200"
      onClick={onOpenDecisionTable}
      role="button"
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      <div className="overflow-hidden bg-white border border-line rounded-lg transition-colors group-hover:border-info-line">
        <div className="overflow-y-auto max-h-48" onWheelCapture={(e) => e.stopPropagation()}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-surface-subtle">
              <tr>
                <th className="px-3 py-2 font-medium text-left text-fg-secondary border-b border-line">
                  #
                </th>
                {columnNames.map((columnName) => (
                  <th
                    key={columnName}
                    className="px-3 py-2 font-medium text-left text-fg-secondary border-b border-line"
                  >
                    {columnName}
                  </th>
                ))}
                <th className="px-3 py-2 font-medium text-left text-fg-secondary border-b border-line">
                  {t('viralRecording.labels.result')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule, index) => (
                <tr
                  key={rule._id ?? index}
                  className={index % 2 === 0 ? 'bg-white' : 'bg-surface-subtle/50'}
                >
                  <td className="px-3 py-2 border-b border-line-subtle text-fg-secondary">
                    {index + 1}
                  </td>
                  {columnNames.map((columnName) => (
                    <td
                      key={columnName}
                      className="px-3 py-2 border-b border-line-subtle text-foreground"
                    >
                      {/* `||` (not `??`) on purpose: an empty-string condition
                          shows the placeholder, same as a missing column. */}
                      {getRuleInputValue(rule.inputs, columnName) || (
                        <span className="italic text-fg-subtle">
                          {t('viralRecording.modals.decisionTable.emptyCondition')}
                        </span>
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-2 border-b border-line-subtle text-foreground">
                    {rule.output || (
                      <span className="italic text-fg-subtle">
                        {t('viralRecording.modals.decisionTable.emptyCondition')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex items-center justify-center mt-2">
        <span className="text-xs text-muted-foreground group-hover:text-info transition-colors flex items-center gap-1">
          <MousePointerClick className="h-3 w-3" />
          {t('viralRecording.labels.clickToEdit') || 'Click to edit'}
        </span>
      </div>
    </div>
  );
}
